import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom"; // Add this line
import {
  AlertTriangle,
  ArrowRight,
  Lock,
  Database,
  Activity,
  Mail,
  Bug,
  UserX,
  Clock,
  Calendar,
  ChevronDown,
  // CalendarDays,
  // PhoneCall,
} from "lucide-react";
import Footer from "../../../components/Footer";

const IncidentResponse = () => {
  const navigate = useNavigate(); // Initialize navigate
  useEffect(() => {
    // Reveal Animation Logic
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("active");
        }
      });
    }, observerOptions);

    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    // Navbar Scroll Effect
    const navbar = document.getElementById("navbar");
    const handleScroll = () => {
      if (window.pageYOffset > 100) {
        navbar?.classList.add("shadow-lg", "shadow-emerald-500/5");
      } else {
        navbar?.classList.remove("shadow-lg", "shadow-emerald-500/5");
      }
    };

    // Parallax Orbs Logic
    const handleMouseMove = (e) => {
      const orbs = document.querySelectorAll(".orb-effect");
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;

      orbs.forEach((orb, index) => {
        const speed = (index + 1) * 20;
        const xOffset = (0.5 - x) * speed;
        const yOffset = (0.5 - y) * speed;
        orb.style.transform = `translate(${xOffset}px, ${yOffset}px)`;
      });
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Incident Response Services | Synthera Security</title>

        <meta
          name="description"
          content="24/7 incident response services for ransomware, data breaches, malware, email compromise, insider threats, and cyberattack recovery."
        />

        <meta
          name="keywords"
          content="Incident Response, Cyber Incident Response, Ransomware Recovery, Data Breach Response, Malware Response, Digital Forensics, Cybersecurity Services, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Incident Response Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Immediate incident containment, forensic investigation, and business recovery delivered by Synthera Security."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/services/incident-response"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/services/incident-response"
        />
      </Helmet>

      <div className="bg-[#0a0a0f] text-white overflow-x-hidden selection:bg-emerald-500/30 font-['Inter']">
        {/* CSS Injection for custom animations */}
        <style>{`
        .display-font { font-family: 'Space Grotesk', sans-serif; }
        .gradient-bg {
          background: radial-gradient(ellipse at 20% 20%, rgba(16, 185, 129, 0.15) 0%, transparent 50%),
                      radial-gradient(ellipse at 80% 80%, rgba(5, 150, 105, 0.1) 0%, transparent 50%),
                      radial-gradient(ellipse at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 70%),
                      #0a0a0f;
        }
        .noise-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          pointer-events: none; opacity: 0.03; z-index: 50;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.02);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
        }
        .glass-card:hover {
          border-color: rgba(16, 185, 129, 0.3);
          box-shadow: 0 8px 40px 0 rgba(16, 185, 129, 0.1);
        }
        .text-glow { text-shadow: 0 0 40px rgba(16, 185, 129, 0.5); }
        .animated-grid {
          background-size: 50px 50px;
          background-image: linear-gradient(to right, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
          animation: gridMove 20s linear infinite;
        }
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        @keyframes emergencyPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
          50% { box-shadow: 0 0 0 20px rgba(239, 68, 68, 0); }
        }
        .emergency-pulse { animation: emergencyPulse 2s infinite; }
        .reveal {
          opacity: 0; transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.5, 0, 0, 1);
        }
        .reveal.active { opacity: 1; transform: translateY(0); }
        .methodology-line {
          position: absolute; left: 24px; top: 60px; bottom: 0; width: 2px;
          background: linear-gradient(to bottom, rgba(16, 185, 129, 0.5), transparent);
        }
      `}</style>

        <div className="noise-overlay"></div>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden gradient-bg">
          <div className="absolute inset-0 animated-grid opacity-30"></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse orb-effect"></div>
          <div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse orb-effect"
            style={{ animationDelay: "1s" }}
          ></div>

          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 reveal">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-sm text-gray-300">
                24/7 Global Response Available Now
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight reveal display-font transition-delay-100">
              Cyberattacks don't wait.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-500 text-glow">
                Neither do we.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed reveal transition-delay-200">
              Immediate incident containment, forensic investigation, and
              business recovery. Our elite response team acts within minutes,
              not hours.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center reveal transition-delay-300">
              <button
                onClick={() => navigate("/under-attack")} // Navigation added here
                className="emergency-pulse bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-full text-lg font-semibold flex items-center gap-3 transition-all hover:scale-105 group"
              >
                <AlertTriangle className="w-5 h-5" />
                Active Attack? Call Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => navigate("/contact")}
                className="glass-card hover:bg-white/5 text-white px-8 py-4 rounded-full text-lg font-semibold flex items-center gap-3 transition-all"
              >
                <Calendar className="w-5 h-5" />
                Schedule Readiness Review
              </button>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-6 h-6 text-gray-500" />
          </div>
        </section>

        {/* Threat Types */}
        <section className="py-32 relative">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16 reveal">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 display-font">
                Incidents we neutralize
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl">
                From ransomware to insider threats, our team has seen and
                stopped it all.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <Lock className="text-red-400" />,
                  title: "Ransomware Attacks",
                  desc: "Rapid containment and controlled system recovery.",
                },
                {
                  icon: <Database className="text-emerald-400" />,
                  title: "Data Breaches",
                  desc: "Identify exposed data and preserve forensic evidence.",
                },
                {
                  icon: <Activity className="text-green-400" />,
                  title: "DDoS Attacks",
                  desc: "Mitigation of malicious traffic and rapid restoration.",
                },
                {
                  icon: <Mail className="text-emerald-400" />,
                  title: "Email Compromise",
                  desc: "Account lockdown and prevention of financial loss.",
                },
                {
                  icon: <Bug className="text-green-400" />,
                  title: "Malware & Intrusions",
                  desc: "Deep root cause analysis and threat eradication.",
                },
                {
                  icon: <UserX className="text-yellow-400" />,
                  title: "Insider Threats",
                  desc: "Discreet investigations with minimal disruption.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="glass-card rounded-2xl p-8 hover:-translate-y-2 transition-all group reveal"
                >
                  <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center mb-6 group-hover:bg-emerald-500/10 transition-colors">
                    {item.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-3 display-font">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed mb-4">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Section */}
        <section className="py-32 relative overflow-hidden bg-gradient-to-b from-emerald-950/20 to-transparent">
          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div className="lg:sticky lg:top-32 reveal">
                <h2 className="text-4xl md:text-5xl font-bold mb-6 display-font">
                  Our Proven Methodology
                </h2>
                <div className="glass-card rounded-2xl p-6 border-l-4 border-emerald-500">
                  <div className="flex items-center gap-4 mb-3">
                    <Clock className="w-6 h-6 text-emerald-400" />
                    <span className="text-lg font-semibold">
                      Average Response Time
                    </span>
                  </div>
                  <p className="text-3xl font-bold text-white">
                    &lt; 15 minutes
                  </p>
                </div>
              </div>

              <div className="space-y-8 relative">
                <div className="methodology-line hidden lg:block"></div>
                {[
                  "Detection",
                  "Analysis",
                  "Containment",
                  "Eradication",
                  "Recovery",
                ].map((step, i) => (
                  <div
                    key={i}
                    className="glass-card rounded-2xl p-8 relative lg:ml-12 reveal transition-all"
                  >
                    <div className="absolute -left-12 top-8 w-12 h-12 rounded-full bg-emerald-500  items-center justify-center text-black font-bold text-xl hidden lg:flex">
                      {i + 1}
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-emerald-400 display-font">
                      {step}
                    </h3>
                    <p className="text-gray-300">
                      Elite coordinated action to reduce impact and restore
                      control.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        {/* <section className="py-32 relative overflow-hidden bg-gradient-to-t from-emerald-950/30 to-transparent">
        <div className="max-w-4xl mx-auto px-6 text-center relative reveal">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 display-font">
            Ready when you are.
            <br />
            Especially when you're not.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <button className="emergency-pulse bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-full text-lg font-semibold flex items-center justify-center gap-3 transition-all">
              <PhoneCall className="w-5 h-5" /> Emergency Hotline
            </button>
            <button className="glass-card hover:bg-white/10 text-white px-8 py-4 rounded-full text-lg font-semibold flex items-center justify-center gap-3 border border-white/20 transition-all">
              <CalendarDays className="w-5 h-5" /> Schedule Consultation
            </button>
          </div>
        </div>
      </section> */}
        <Footer />
      </div>
    </>
  );
};

export default IncidentResponse;
